/**
 * Binary wire formats shared by the tile Durable Object, the web/native
 * client and the admin console.
 *
 * Tile WebSocket (server → client), first byte is the message type:
 *   0x01 SNAPSHOT   payload = gzip(65536 palette bytes)
 *   0x02 DIFF       payload = n × { idx: u16 LE, colour: u8 }
 *   0x03 INFO       payload = UTF-8 JSON (subscriber counts etc.)
 * Client → server messages are UTF-8 JSON ({ type: 'ping' }).
 *
 * Globe cell index (GET /v1/globe/cells): little-endian records of
 *   cellToken (16 bytes, NUL padded) · r u8 · g u8 · b u8 · pad u8 · count u32
 */

export const MSG_SNAPSHOT = 0x01;
export const MSG_DIFF = 0x02;
export const MSG_INFO = 0x03;

export interface DiffEntry {
  idx: number;
  colour: number;
}

export function encodeDiff(entries: readonly DiffEntry[]): Uint8Array {
  const out = new Uint8Array(1 + entries.length * 3);
  out[0] = MSG_DIFF;
  let o = 1;
  for (const e of entries) {
    out[o++] = e.idx & 0xff;
    out[o++] = (e.idx >>> 8) & 0xff;
    out[o++] = e.colour & 0xff;
  }
  return out;
}

export function decodeDiff(payload: Uint8Array): DiffEntry[] {
  const n = Math.floor(payload.length / 3);
  const out: DiffEntry[] = new Array(n);
  for (let k = 0; k < n; k++) {
    const o = k * 3;
    out[k] = { idx: payload[o] | (payload[o + 1] << 8), colour: payload[o + 2] };
  }
  return out;
}

export async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('gzip');
  const writer = cs.writable.getWriter();
  void writer.write(bytes as Uint8Array<ArrayBuffer>);
  void writer.close();
  return new Uint8Array(await new Response(cs.readable).arrayBuffer());
}

export async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  void writer.write(bytes as Uint8Array<ArrayBuffer>);
  void writer.close();
  return new Uint8Array(await new Response(ds.readable).arrayBuffer());
}

export async function encodeSnapshotMessage(pixels: Uint8Array): Promise<Uint8Array> {
  const z = await gzip(pixels);
  const out = new Uint8Array(1 + z.length);
  out[0] = MSG_SNAPSHOT;
  out.set(z, 1);
  return out;
}

export function encodeInfoMessage(info: unknown): Uint8Array {
  const json = new TextEncoder().encode(JSON.stringify(info));
  const out = new Uint8Array(1 + json.length);
  out[0] = MSG_INFO;
  out.set(json, 1);
  return out;
}

export type TileMessage =
  | { type: 'snapshot'; pixels: Uint8Array }
  | { type: 'diff'; entries: DiffEntry[] }
  | { type: 'info'; info: unknown };

export async function decodeTileMessage(data: ArrayBuffer | Uint8Array | Blob): Promise<TileMessage> {
  let bytes: Uint8Array;
  if (data instanceof Uint8Array) bytes = data;
  else if (data instanceof ArrayBuffer) bytes = new Uint8Array(data);
  else bytes = new Uint8Array(await data.arrayBuffer());
  const payload = bytes.subarray(1);
  switch (bytes[0]) {
    case MSG_SNAPSHOT:
      return { type: 'snapshot', pixels: await gunzip(payload) };
    case MSG_DIFF:
      return { type: 'diff', entries: decodeDiff(payload) };
    case MSG_INFO:
      return { type: 'info', info: JSON.parse(new TextDecoder().decode(payload)) };
    default:
      throw new Error(`unknown tile message type ${bytes[0]}`);
  }
}

// ---------------------------------------------------------------------------
// Globe cell index
// ---------------------------------------------------------------------------

export interface GlobeCellRecord {
  cell: string;
  r: number;
  g: number;
  b: number;
  count: number;
}

const GLOBE_RECORD_SIZE = 24;

export function encodeGlobeCells(cells: readonly GlobeCellRecord[]): Uint8Array {
  const out = new Uint8Array(cells.length * GLOBE_RECORD_SIZE);
  const view = new DataView(out.buffer);
  const enc = new TextEncoder();
  cells.forEach((c, k) => {
    const o = k * GLOBE_RECORD_SIZE;
    const tok = enc.encode(c.cell);
    out.set(tok.subarray(0, 16), o);
    out[o + 16] = c.r;
    out[o + 17] = c.g;
    out[o + 18] = c.b;
    out[o + 19] = 0;
    view.setUint32(o + 20, c.count >>> 0, true);
  });
  return out;
}

export function decodeGlobeCells(bytes: Uint8Array): GlobeCellRecord[] {
  const n = Math.floor(bytes.length / GLOBE_RECORD_SIZE);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const dec = new TextDecoder();
  const out: GlobeCellRecord[] = new Array(n);
  for (let k = 0; k < n; k++) {
    const o = k * GLOBE_RECORD_SIZE;
    let end = o;
    while (end < o + 16 && bytes[end] !== 0) end++;
    out[k] = {
      cell: dec.decode(bytes.subarray(o, end)),
      r: bytes[o + 16],
      g: bytes[o + 17],
      b: bytes[o + 18],
      count: view.getUint32(o + 20, true),
    };
  }
  return out;
}

// ---------------------------------------------------------------------------
// Base64 helpers (avatars, dev tokens) that work in Workers, browsers and Node.
// ---------------------------------------------------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  return base64ToBytes(b64);
}
